import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../components/site/Page";
import outdoorBasket from "../assets/outdoor-basket.webp";

const title = "Fritidsverksamhet | Swedish International School Estepona";
const description =
  "Fritids med svensktalande pedagoger i Estepona: sport, skapande, musik och drama i en trygg miljö efter skoldagen, fram till 16.30.";

export const Route = createFileRoute("/fritidsverksamhet")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: absoluteUrl("/fritidsverksamhet") },
      { property: "og:locale", content: "sv_SE" },
      ...ogImage(),
    ],
    links: seoLinks("/fritidsverksamhet"),
  }),
  component: Fritids,
});

function Fritids() {
  return (
    <>
      <PageHero
        eyebrow="Efter skoldagen"
        title="Fritidsverksamhet"
        intro="Fritids vid Swedish International School är en viktig del av skolans verksamhet och ger eleverna möjlighet att delta i olika aktiviteter och ha roligt tillsammans."
      />

      <Prose>
        <img
          src={outdoorBasket}
          alt="Elever som spelar basket på skolans utegård"
          loading="lazy"
          className="rounded-xl shadow-soft"
          width={810}
          height={540}
        />

        <h2>Aktiviteter för alla intressen</h2>
        <p>
          På fritids finns en mängd olika aktiviteter som eleverna kan välja mellan. Det finns
          sportaktiviteter som fotboll, basket och volleyboll, kreativa aktiviteter som teckning och
          målning, samt musik- och dramaaktiviteter som teater och sång. Eleverna väljer själva vilka
          aktiviteter de vill delta i och det finns något för alla intressen.
        </p>

        <h2>Gemenskap och vänskap</h2>
        <p>
          Fritidsverksamheten är också en möjlighet för eleverna att lära känna varandra bättre och
          bygga starka relationer. Eleverna kommer från olika delar av Spanien och världen, men har
          alla gemensamt att de talar svenska. Fritidsaktiviteterna ger eleverna en chans att umgås
          med andra svensktalande barn och skapa vänskapsband.
        </p>

        <h2>Nya färdigheter</h2>
        <p>
          Eleverna får möjlighet att prova på olika aktiviteter och upptäcka nya intressen som de kan
          fortsätta att utveckla efter skoltid. För föräldrar innebär fritids en trygg och
          stimulerande miljö för barnen efter skoldagen, med svensktalande pedagoger.
        </p>

        <h2>Dagens rytm</h2>
        <p>
          När skoldagen är slut samlas barnen med pedagogerna innan eftermiddagen drar igång. Det
          finns tid för mellanmål, för att komma ut på gården och för lugnare stunder inomhus för den
          som vill varva ner. Fritids håller öppet fram till 16.30 under skoldagar och följer samma{" "}
          <a className="text-primary link-quiet" href="/lasarstider">läsårstider</a> som skolan,
          vilket innebär att verksamheten är stängd under lov och helgdagar.
        </p>

        <h2>Bra att ha med sig</h2>
        <ul>
          <li>Kläder som tål lek utomhus och skor som går att springa i.</li>
          <li>Solskydd och keps under de varmare månaderna.</li>
          <li>Vattenflaska märkt med namn.</li>
          <li>Ombyte för de yngsta barnen.</li>
        </ul>

        <h2>Kontakt under eftermiddagen</h2>
        <p>
          Meddela skolan om ert barn ska hämtas av någon annan än vanligt eller gå hem tidigare än
          planerat. Vid frågor om plats, tider eller avgifter är ni välkomna att mejla{" "}
          <a className="text-primary link-quiet" href="mailto:info@sweintschool.com">info@sweintschool.com</a>.
          Kostnaden för fritids framgår på{" "}
          <a className="text-primary link-quiet" href="/avgifter">avgiftssidan</a> och anmäls i
          samband med{" "}
          <a className="text-primary link-quiet" href="/ansokan">skolansökan</a>.
        </p>
      </Prose>

      <FaqBlock items={faqFor("fritids", "sv")} locale={"sv"} />

      <CtaBand />
      <div className="h-20" />
    </>
  );
}
